import { defaultTo } from 'lodash';
import { useState } from 'react';
import './text-input.scss';
import { Icon, TextInput, Tooltip } from '@/components/atoms';
import useTextInputValidation from '@/components/molecules/TextInputField/useTextInputValidation-hook';

/**
 * TextInputField component renders a text input field with various configurations.
 *
 * @param {Object} props - The properties object.
 * @param {string} props.value - The current value of the input field.
 * @param {function} props.updateValue - Function to update the value of the input field.
 * @param {string} props.label - The label for the input field.
 * @param {Object} props.schema - The schema defining the input field type and validation.
 * @param {Object} props.uiSchema - The UI schema defining additional UI properties.
 * @param {boolean} [props.required=false] - Whether the input field is required.
 * @param {boolean} [props.disabled=false] - Whether the input field is disabled.
 * @param {boolean} props.visible - Whether the input field is visible.
 *
 * @returns {JSX.Element|null} The rendered TextInputField component or null if not visible.
 */

const TextInputField = ({
  value,
  updateValue,
  label,
  schema,
  uiSchema,
  required = false,
  disabled = false,
  visible,
}) => {
  const { onChange } = useTextInputValidation(label, uiSchema?.validationRules);
  const [error, setError] = useState();
  const [isFocused, setIsFocused] = useState(false);

  const disableButton = disabled || uiSchema?.readOnly ? 'disable-button' : '';

  // Helper function to get the updated value based on schema type
  const getUpdatedValue = (val) => {
    if (schema?.type === 'number' || schema?.type === 'integer') {
      return val ? parseInt(val) : val;
    }
    return val;
  };

  // Helper function to determine the input type
  const getInputType = () => (schema?.type === 'integer' ? 'number' : schema?.type || 'text');

  // Helper function to check if a key should be restricted
  const shouldRestrictKey = (key) =>
    (schema?.restrictDecimal && ['.', ',', 'e', '-'].includes(key)) ||
    (!value && key === '0') ||
    (schema?.type === 'number' && ['e', 'E', '+', '-'].includes(key)) ||
    ['ArrowUp', 'ArrowDown'].includes(key);

  const handleKeyDown = (e) => {
    if (shouldRestrictKey(e.key)) e.preventDefault();
  };

  // Helper function to handle onChange logic
  const handleOnChange = (val) => {
    const updatedValue = getUpdatedValue(val);
    const validationError = onChange(val);
    setError(validationError);
    updateValue(updatedValue, !!validationError);
  };

  // Helper function to render tooltip
  const renderTooltip = (tooltipText) =>
    tooltipText?.length > 0 && (
      <div className="tooltip-text">
        <Tooltip text={tooltipText}>
          <Icon alt="" icon="info" size="small" />
        </Tooltip>
      </div>
    );

  // Helper function to render help text
  const renderHelpText = (helpText) =>
    !error &&
    helpText && (
      <div className="input-help-text items-center">
        <Tooltip text={helpText} className="help-tooltip">
          <Icon icon="info" size="small" />
        </Tooltip>
        {helpText}
      </div>
    );

  if (!visible) return null;

  return (
    <>
      <div className={`input-text-wrapper ${uiSchema?.className}`}>
        <TextInput
          required={required}
          className={`${uiSchema?.className ?? ''} ${disableButton} inputType-${getInputType()}`}
          disabled={disabled || uiSchema?.readOnly || uiSchema?.disabled}
          maxLength={uiSchema?.maxLength ?? undefined}
          type={getInputType()}
          error={error}
          label={label}
          onChange={handleOnChange}
          isDark={uiSchema?.isDark || false}
          value={defaultTo(value, undefined)}
          placeholderText={uiSchema?.placeholder ?? ''}
          helperText={uiSchema?.helperText}
          onBlur={() => !isFocused && setIsFocused(true)}
          handleKeyDown={handleKeyDown}
        />
        {renderTooltip(uiSchema?.tooltipText)}
      </div>
      {renderHelpText(uiSchema?.helpText)}
    </>
  );
};

export default TextInputField;